import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatMoney, todayISO, formatDateTime } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Wallet, Banknote, TrendingDown, TrendingUp, Truck, Gauge, AlertTriangle, Users, Pencil, X, UserCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Expenses() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Only managers get locked to their holder; admin can switch freely
  const lockedHolderId = (user?.role === "manager") ? (user?.holder_id || null) : null;

  const [holders, setHolders] = useState([]);
  const [cats, setCats] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [holderId, setHolderId] = useState("");
  const [ledger, setLedger] = useState(null);

  // forms
  const [tab, setTab] = useState("expense"); // expense | cash-in
  const [expense, setExpense] = useState({
    date: todayISO(), category: "", amount: 0, description: "", reference: "",
    vehicle_id: "", trip_km: "", litres: "",
  });
  const [cashIn, setCashIn] = useState({
    date: todayISO(), amount: 0, direction: "in", description: "",
  });
  const [newCat, setNewCat] = useState("");
  const [monthFilter, setMonthFilter] = useState(""); // "" = all-time, else "YYYY-MM"
  const [showVehicleQuickAdd, setShowVehicleQuickAdd] = useState(false);
  const [newVehicle, setNewVehicle] = useState({ number: "", name: "", expected_avg_kmpl: 0 });
  const [busy, setBusy] = useState(false);

  const isDiesel = (expense.category || "").toLowerCase().includes("diesel");
  const isSalary = (expense.category || "").toLowerCase().includes("salary");
  const isFood = (expense.category || "").toLowerCase().includes("food");
  const selectedVehicle = vehicles.find((v) => v.id === expense.vehicle_id);
  const currOdo = Number(expense.trip_km) || 0;
  const litres = Number(expense.litres) || 0;
  const [prevOdo, setPrevOdo] = useState(null);
  const [prevOdoDate, setPrevOdoDate] = useState(null);
  const kmRun = prevOdo !== null && currOdo > prevOdo ? +(currOdo - prevOdo).toFixed(2) : 0;
  const computedAvg = kmRun > 0 && litres > 0 ? +(kmRun / litres).toFixed(2) : 0;
  const expectedAvg = Number(selectedVehicle?.expected_avg_kmpl) || 0;
  const deviationPct = expectedAvg > 0 && computedAvg > 0
    ? +(((computedAvg - expectedAvg) * 100) / expectedAvg).toFixed(2)
    : null;
  const deviationFlag = deviationPct === null ? null
    : Math.abs(deviationPct) >= 15 ? (deviationPct < 0 ? "low" : "high") : "ok";

  // Fetch previous odometer whenever vehicle changes
  useEffect(() => {
    if (!isDiesel || !expense.vehicle_id) { setPrevOdo(null); setPrevOdoDate(null); return; }
    api.get(`/vehicles/${expense.vehicle_id}/last-odometer`)
      .then((r) => { setPrevOdo(r.data.last_odometer_km); setPrevOdoDate(r.data.last_date); })
      .catch(() => { setPrevOdo(null); setPrevOdoDate(null); });
  }, [expense.vehicle_id, isDiesel]);

  const loadMasters = async () => {
    const [h, c, v, e] = await Promise.all([
      api.get("/cash-holders"),
      api.get("/expense-categories"),
      api.get("/vehicles?active=true"),
      api.get("/employees?active=true"),
    ]);
    const list = lockedHolderId ? h.data.filter((x) => x.id === lockedHolderId) : h.data;
    setHolders(list);
    setCats(c.data);
    setVehicles(v.data);
    setEmployees(e.data);
    if (!holderId && list.length) {
      const preferred = user?.holder_id && list.find((x) => x.id === user.holder_id);
      setHolderId(preferred ? preferred.id : (lockedHolderId || list[0].id));
    }
  };

  const loadLedger = async (hid) => {
    if (!hid) return;
    const { data } = await api.get(`/cash-holders/${hid}/ledger`);
    setLedger(data);
  };

  useEffect(() => { loadMasters(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (holderId) loadLedger(holderId); /* eslint-disable-next-line */ }, [holderId]);

  const holder = useMemo(() => holders.find((h) => h.id === holderId), [holders, holderId]);

  // Month filter — build list of "YYYY-MM" present in ledger, plus apply filter to rows + category totals
  const allRows = ledger?.rows || [];
  const monthOptions = useMemo(() => {
    const set = new Set();
    allRows.forEach((r) => { if (r.date) set.add(r.date.slice(0, 7)); });
    return Array.from(set).sort().reverse();
  }, [allRows]);

  const filteredRows = useMemo(() => {
    if (!monthFilter) return allRows;
    return allRows.filter((r) => (r.date || "").startsWith(monthFilter));
  }, [allRows, monthFilter]);

  const filteredCategoryTotals = useMemo(() => {
    const totals = {};
    filteredRows.forEach((r) => {
      if (r.kind === "expense" && r.category) {
        totals[r.category] = (totals[r.category] || 0) + Math.abs(r.amount);
      }
    });
    return Object.entries(totals)
      .map(([category, total]) => ({ category, total: Math.round(total * 100) / 100 }))
      .sort((a, b) => b.total - a.total);
  }, [filteredRows]);

  const filteredTotals = useMemo(() => {
    let inAmt = 0, expAmt = 0;
    filteredRows.forEach((r) => {
      if (r.amount > 0) inAmt += r.amount;
      if (r.kind === "expense") expAmt += Math.abs(r.amount);
    });
    return { in: Math.round(inAmt * 100) / 100, expenses: Math.round(expAmt * 100) / 100 };
  }, [filteredRows]);

  const monthLabel = (ym) => {
    if (!ym) return "All time";
    const [y, m] = ym.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  };

  const saveExpense = async (e) => {
    e.preventDefault();
    if (!expense.category || Number(expense.amount) <= 0) {
      toast.error("Category and amount required"); return;
    }
    if (isDiesel && !expense.vehicle_id && (Number(expense.trip_km) > 0 || Number(expense.litres) > 0)) {
      toast.error("Vehicle required when trip km or litres are entered"); return;
    }
    if (isSalary && !expense.employee_id) {
      toast.error("Select an employee for a salary expense"); return;
    }
    if (isFood && !expense.employee_id) {
      toast.error("Select an employee for a food expense"); return;
    }
    setBusy(true);
    try {
      const payload = {
        date: expense.date,
        category: expense.category,
        amount: Number(expense.amount),
        description: expense.description,
        reference: expense.reference,
        holder_id: holderId,
        vehicle_id: isDiesel ? (expense.vehicle_id || null) : null,
        trip_km: isDiesel && currOdo > 0 ? currOdo : null,
        litres: isDiesel && litres > 0 ? litres : null,
        employee_id: (isSalary || isFood) ? (expense.employee_id || null) : null,
      };
      await api.post("/expenses", payload);
      if (isDiesel && deviationFlag && deviationFlag !== "ok") {
        toast.warning(
          `Saved · Deviation alert: ${computedAvg} kmpl vs expected ${expectedAvg} (${deviationPct > 0 ? "+" : ""}${deviationPct}%)`,
          { duration: 6000 }
        );
      } else {
        toast.success("Expense recorded");
      }
      setExpense({
        date: todayISO(), category: expense.category, amount: 0,
        description: "", reference: "", vehicle_id: "", trip_km: "", litres: "",
        employee_id: "",
      });
      loadLedger(holderId);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const saveCashTransfer = async (e) => {
    e.preventDefault();
    if (Number(cashIn.amount) <= 0) { toast.error("Amount required"); return; }
    setBusy(true);
    try {
      await api.post("/cash-transfers", {
        ...cashIn, holder_id: holderId, amount: Number(cashIn.amount),
      });
      toast.success(cashIn.direction === "in" ? "Cash given to holder" : "Cash returned by holder");
      setCashIn({ date: todayISO(), amount: 0, direction: cashIn.direction, description: "" });
      loadLedger(holderId);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try {
      await api.post("/expense-categories", { name: newCat.trim() });
      setNewCat("");
      loadMasters();
      toast.success("Category added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addVehicleQuick = async (e) => {
    e.preventDefault();
    if (!newVehicle.number.trim()) { toast.error("Vehicle number required"); return; }
    try {
      const { data } = await api.post("/vehicles", {
        ...newVehicle,
        expected_avg_kmpl: Number(newVehicle.expected_avg_kmpl) || 0,
        active: true, notes: "",
      });
      toast.success("Vehicle added");
      setNewVehicle({ number: "", name: "", expected_avg_kmpl: 0 });
      setShowVehicleQuickAdd(false);
      const v = await api.get("/vehicles?active=true");
      setVehicles(v.data);
      setExpense((prev) => ({ ...prev, vehicle_id: data.id }));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteRow = async (row) => {
    if (!isAdmin) return;
    if (!window.confirm("Delete this entry?")) return;
    const url = row.ref_type === "expense" ? `/expenses/${row.id}`
              : row.ref_type === "cash_transfer" ? `/cash-transfers/${row.id}`
              : null;
    if (!url) { toast.error("Delete customer payments from Payments page"); return; }
    try { await api.delete(url); loadLedger(holderId); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Edit expense notes (description + reference). Other expense fields stay immutable.
  const [editRow, setEditRow] = useState(null); // { id, description, reference }
  const openEdit = async (row) => {
    try {
      // pull the full expense so we get current description + reference
      const { data } = await api.get(`/expenses?holder_id=${holderId}&limit=2000`);
      const doc = (data || []).find((e) => e.id === row.id);
      setEditRow({
        id: row.id,
        description: doc?.description || "",
        reference: doc?.reference || "",
        category: doc?.category || "",
        amount: doc?.amount || 0,
        date: doc?.date || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editRow) return;
    setBusy(true);
    try {
      await api.patch(`/expenses/${editRow.id}`, {
        description: editRow.description,
        reference: editRow.reference,
      });
      toast.success("Notes updated");
      setEditRow(null);
      loadLedger(holderId);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Holder selector + balance cards */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="small-label text-gray-500">Cash Holder</label>
        <select className="input-lg w-auto min-w-[260px]" value={holderId}
          onChange={(e) => setHolderId(e.target.value)} data-testid="holder-select"
          disabled={!!lockedHolderId}>
          {holders.map((h) => (
            <option key={h.id} value={h.id}>
              {h.name}{h.location ? ` — ${h.location}` : ""}
            </option>
          ))}
        </select>
        {lockedHolderId && (
          <span className="small-label text-gray-500">Showing your cash ledger</span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="small-label text-gray-500">Cash in hand</div>
            <Wallet size={20} className="text-gray-400" />
          </div>
          <div className={`data-num text-3xl ${ (ledger?.closing_balance || 0) >= 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}
            data-testid="holder-balance">
            ₹ {formatMoney(ledger?.closing_balance || 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Holder: <span className="font-semibold">{holder?.name || "—"}</span>
            {holder?.location ? ` · ${holder.location}` : ""}
          </div>
        </div>
        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="small-label text-gray-500">Total received</div>
            <TrendingUp size={20} className="text-gray-400" />
          </div>
          <div className="data-num text-3xl text-[#15803D]">₹ {formatMoney(ledger?.total_in || 0)}</div>
          <div className="text-xs text-gray-500 mt-1">From owner + customers</div>
        </div>
        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="small-label text-gray-500">Total expenses</div>
            <TrendingDown size={20} className="text-gray-400" />
          </div>
          <div className="data-num text-3xl text-[#B91C1C]">₹ {formatMoney(ledger?.total_expenses || 0)}</div>
          <div className="text-xs text-gray-500 mt-1">All categories</div>
        </div>
      </div>

      {/* Entry tabs */}
      <div className="card-flat">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setTab("expense")}
            className={`px-5 py-3 text-sm font-bold tracking-wider uppercase ${tab === "expense" ? "bg-black text-white" : "bg-white text-gray-500 hover:text-black"}`}
            data-testid="tab-expense"
          >Record Expense</button>
          {isAdmin && (
            <button
              onClick={() => setTab("cash-in")}
              className={`px-5 py-3 text-sm font-bold tracking-wider uppercase ${tab === "cash-in" ? "bg-black text-white" : "bg-white text-gray-500 hover:text-black"}`}
              data-testid="tab-cash-in"
            >Cash Owner ↔ Holder</button>
          )}
        </div>

        {tab === "expense" && (
          <form onSubmit={saveExpense} className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="expense-form">
            <div>
              <label className="small-label block mb-2">Date</label>
              <input type="date" className="input-lg" value={expense.date}
                onChange={(e) => setExpense({ ...expense, date: e.target.value })} required
                data-testid="expense-date" />
            </div>
            <div>
              <label className="small-label block mb-2">Category</label>
              <select className="input-lg" value={expense.category}
                onChange={(e) => setExpense({ ...expense, category: e.target.value })}
                required data-testid="expense-category">
                <option value="">— Select —</option>
                {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="small-label block mb-2">Amount ₹</label>
              <input type="number" step="0.01" className="input-lg" value={expense.amount}
                onChange={(e) => setExpense({ ...expense, amount: e.target.value })}
                required data-testid="expense-amount" />
            </div>
            <div>
              <label className="small-label block mb-2">Reference</label>
              <input className="input-lg" value={expense.reference}
                onChange={(e) => setExpense({ ...expense, reference: e.target.value })}
                placeholder="bill no, etc." />
            </div>

            {isDiesel && (
              <div className="sm:col-span-2 lg:col-span-4 card-flat p-4 bg-[#FEF3C7]/40 border-[#FDE68A]" data-testid="diesel-block">
                <div className="flex items-center gap-2 mb-3">
                  <Truck size={18} className="text-[#B45309]" />
                  <div className="font-heading font-bold text-sm tracking-wider uppercase text-[#B45309]">
                    Diesel · Vehicle tracking
                  </div>
                  <span className="text-[10px] text-[#92400E] normal-case tracking-normal font-medium">(optional — fill later from Diesel module)</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="sm:col-span-2">
                    <label className="small-label block mb-1">Vehicle</label>
                    <div className="flex gap-2">
                      <select className="input-lg flex-1" value={expense.vehicle_id}
                        onChange={(e) => {
                          if (e.target.value === "__add__") { setShowVehicleQuickAdd(true); }
                          else { setExpense({ ...expense, vehicle_id: e.target.value }); }
                        }}
                        data-testid="expense-vehicle">
                        <option value="">— Select —</option>
                        {vehicles.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.number}{v.name ? ` · ${v.name}` : ""} — exp {v.expected_avg_kmpl} kmpl
                          </option>
                        ))}
                        <option value="__add__">+ Add new vehicle…</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="small-label block mb-1">Current Odometer (km)</label>
                    <input type="number" step="0.1" className="input-lg data-num text-right"
                      value={expense.trip_km}
                      onChange={(e) => setExpense({ ...expense, trip_km: e.target.value })}
                      placeholder={prevOdo !== null ? `> ${prevOdo}` : "current reading"}
                      data-testid="expense-trip-km" />
                    {prevOdo !== null && (
                      <div className="text-xs text-gray-500 mt-1">
                        Previous: <span className="data-num font-bold">{prevOdo} km</span>
                        {prevOdoDate ? <> · {prevOdoDate}</> : null}
                        {kmRun > 0 && <> · <span className="text-[#15803D] font-bold">Run +{kmRun} km</span></>}
                      </div>
                    )}
                    {prevOdo === null && isDiesel && expense.vehicle_id && (
                      <div className="text-xs text-[#B45309] mt-1">First entry — set baseline. Next fill will compute average.</div>
                    )}
                  </div>
                  <div>
                    <label className="small-label block mb-1">Litres</label>
                    <input type="number" step="0.01" className="input-lg data-num text-right"
                      value={expense.litres}
                      onChange={(e) => setExpense({ ...expense, litres: e.target.value })}
                      placeholder="diesel filled"
                      data-testid="expense-litres" />
                  </div>
                </div>

                {computedAvg > 0 && (
                  <div className="mt-3 flex items-center gap-3 flex-wrap" data-testid="diesel-avg-preview">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white border border-gray-300">
                      <Gauge size={14} className="text-gray-500" />
                      <span className="text-xs text-gray-500">Avg</span>
                      <span className="data-num text-base font-bold">{computedAvg} kmpl</span>
                    </div>
                    {expectedAvg > 0 && (
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white border border-gray-300">
                        <span className="text-xs text-gray-500">Expected</span>
                        <span className="data-num text-base font-bold">{expectedAvg} kmpl</span>
                      </div>
                    )}
                    {deviationFlag && (
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm font-bold text-sm ${
                        deviationFlag === "low" ? "stock-mt"
                        : deviationFlag === "high" ? "stock-filled"
                        : "bg-gray-100 text-gray-600"
                      }`}>
                        {(deviationFlag === "low" || deviationFlag === "high") && <AlertTriangle size={14} />}
                        {deviationPct > 0 ? "+" : ""}{deviationPct}%{" "}
                        {deviationFlag === "low" ? "below expected" : deviationFlag === "high" ? "above expected" : "within range"}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {isSalary && (
              <div className="sm:col-span-2 lg:col-span-4 card-flat p-4 bg-[#DCFCE7]/40 border-[#86EFAC]" data-testid="salary-block">
                <div className="flex items-center gap-2 mb-3">
                  <Users size={18} className="text-[#15803D]" />
                  <div className="font-heading font-bold text-sm tracking-wider uppercase text-[#15803D]">
                    Salary · Employee
                  </div>
                </div>
                <div>
                  <label className="small-label block mb-1">Employee *</label>
                  <select className="input-lg" value={expense.employee_id}
                    onChange={(e) => setExpense({ ...expense, employee_id: e.target.value })}
                    data-testid="expense-employee">
                    <option value="">— Select —</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}{emp.role ? ` · ${emp.role}` : ""} —{" "}
                        {emp.wage_type === "monthly"
                          ? `₹ ${emp.monthly_salary}/mo`
                          : `₹ ${emp.daily_wage}/day`}
                      </option>
                    ))}
                  </select>
                  {expense.employee_id && (
                    <div className="text-xs text-gray-500 mt-1.5">
                      Will be recorded against this employee&apos;s ledger.{" "}
                      <Link to={`/employees/${expense.employee_id}/ledger`} className="underline font-semibold">View ledger →</Link>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isFood && (
              <div className="sm:col-span-2 lg:col-span-4 card-flat p-4 bg-[#FEF3C7]/60 border-[#FDE68A]" data-testid="food-block">
                <div className="flex items-center gap-2 mb-3">
                  <Users size={18} className="text-[#B45309]" />
                  <div className="font-heading font-bold text-sm tracking-wider uppercase text-[#B45309]">
                    Food · Employee
                  </div>
                </div>
                <div>
                  <label className="small-label block mb-1">Employee *</label>
                  <select className="input-lg" value={expense.employee_id}
                    onChange={(e) => setExpense({ ...expense, employee_id: e.target.value })}
                    data-testid="expense-employee-food">
                    <option value="">— Select —</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}{emp.role ? ` · ${emp.role}` : ""}
                      </option>
                    ))}
                  </select>
                  {expense.employee_id && (
                    <div className="text-xs text-gray-500 mt-1.5">
                      Kept in a <b>separate food ledger</b> — never mixed with salary.{" "}
                      <Link to={`/employees/${expense.employee_id}/food-ledger`} className="underline font-semibold">View food ledger →</Link>
                    </div>
                  )}
                  <div className="text-[10px] text-gray-500 mt-1">
                    Tip: create a category with the word &quot;food&quot; in it (e.g. &quot;Food · staff&quot;) to trigger this per-employee tagging.
                  </div>
                </div>
              </div>
            )}

            <div className="lg:col-span-3">
              <label className="small-label block mb-2">Description</label>
              <input className="input-lg" value={expense.description}
                onChange={(e) => setExpense({ ...expense, description: e.target.value })} />
            </div>
            <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between flex-wrap gap-3">
              <form onSubmit={addCategory} className="flex items-center gap-2">
                <input className="input-lg h-10" placeholder="+ new category" value={newCat}
                  onChange={(e) => setNewCat(e.target.value)} data-testid="new-category-input" />
                <button type="submit" className="border border-gray-300 px-3 h-10 rounded-sm text-sm font-semibold hover:bg-gray-50 flex items-center gap-1" data-testid="add-category-btn">
                  <Plus size={14} /> Add
                </button>
                <Link to="/vehicles" className="ml-2 text-xs text-gray-500 hover:text-black underline">Manage vehicles</Link>
              </form>
              <button className="btn-primary h-11 flex items-center gap-2" disabled={busy}
                data-testid="expense-submit">
                <Save size={16} /> Save Expense
              </button>
            </div>
          </form>
        )}

        {tab === "cash-in" && isAdmin && (
          <form onSubmit={saveCashTransfer} className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cash-transfer-form">
            <div>
              <label className="small-label block mb-2">Date</label>
              <input type="date" className="input-lg" value={cashIn.date}
                onChange={(e) => setCashIn({ ...cashIn, date: e.target.value })} required />
            </div>
            <div>
              <label className="small-label block mb-2">Direction</label>
              <select className="input-lg" value={cashIn.direction}
                onChange={(e) => setCashIn({ ...cashIn, direction: e.target.value })}>
                <option value="in">Owner → Holder (give cash)</option>
                <option value="out">Holder → Owner (return cash)</option>
              </select>
            </div>
            <div>
              <label className="small-label block mb-2">Amount ₹</label>
              <input type="number" step="0.01" className="input-lg" value={cashIn.amount}
                onChange={(e) => setCashIn({ ...cashIn, amount: e.target.value })} required
                data-testid="cashtransfer-amount" />
            </div>
            <div>
              <label className="small-label block mb-2">Description</label>
              <input className="input-lg" value={cashIn.description}
                onChange={(e) => setCashIn({ ...cashIn, description: e.target.value })} />
            </div>
            <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
              <button className="btn-primary h-11 flex items-center gap-2" disabled={busy}
                data-testid="cashtransfer-submit">
                <Banknote size={16} /> Save Transfer
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Quick-add Vehicle modal */}
      {showVehicleQuickAdd && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowVehicleQuickAdd(false)}>
          <form onSubmit={addVehicleQuick} className="bg-white rounded-sm w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="vehicle-quickadd-form">
            <div>
              <div className="small-label text-gray-500">Quick add</div>
              <h3 className="font-heading font-black text-xl">New Vehicle</h3>
            </div>
            <div>
              <label className="small-label block mb-1">Vehicle Number *</label>
              <input className="input-lg uppercase" value={newVehicle.number}
                onChange={(e) => setNewVehicle({ ...newVehicle, number: e.target.value.toUpperCase() })}
                placeholder="MH14KQ8335" required data-testid="qa-vehicle-number" />
            </div>
            <div>
              <label className="small-label block mb-1">Name / Model</label>
              <input className="input-lg" value={newVehicle.name}
                onChange={(e) => setNewVehicle({ ...newVehicle, name: e.target.value })} />
            </div>
            <div>
              <label className="small-label block mb-1">Expected Avg (km/L) *</label>
              <input type="number" step="0.1" className="input-lg data-num text-right"
                value={newVehicle.expected_avg_kmpl}
                onChange={(e) => setNewVehicle({ ...newVehicle, expected_avg_kmpl: e.target.value })}
                required data-testid="qa-vehicle-avg" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowVehicleQuickAdd(false)} className="h-11 px-4 border border-gray-300 rounded-sm text-sm font-semibold">Cancel</button>
              <button type="submit" className="btn-primary h-11 px-5">Add & Select</button>
            </div>
          </form>
        </div>
      )}

      {/* Category totals */}
      {allRows.length > 0 && (
        <div className="card-flat">
          <div className="p-4 border-b border-gray-200 flex items-center gap-3 flex-wrap">
            <span className="small-label text-gray-500">
              Expenses by Category · <span className="font-bold text-black">{monthLabel(monthFilter)}</span>
            </span>
            <div className="ml-auto flex items-center gap-2">
              <label className="small-label text-gray-500">Month</label>
              <select
                className="input-lg h-9 w-44"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                data-testid="expense-month-filter"
              >
                <option value="">All time</option>
                {monthOptions.map((m) => (
                  <option key={m} value={m}>{monthLabel(m)}</option>
                ))}
              </select>
              {monthFilter && (
                <button
                  type="button"
                  onClick={() => setMonthFilter("")}
                  className="text-xs text-gray-500 hover:text-black underline"
                  data-testid="clear-month-filter"
                >
                  clear
                </button>
              )}
            </div>
          </div>
          <div className="p-5 space-y-3">
            <div className="flex flex-wrap gap-2">
              {filteredCategoryTotals.length === 0 && (
                <div className="text-sm text-gray-400">No expenses in this period.</div>
              )}
              {filteredCategoryTotals.map((c) => (
                <div key={c.category} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#FEE2E2] text-[#B91C1C] text-sm" data-testid={`cat-total-${c.category}`}>
                  <span className="font-semibold">{c.category}</span>
                  <span className="data-num">₹ {formatMoney(c.total)}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-600 pt-2 border-t border-gray-100">
              <span>Cash In: <span className="data-num font-bold text-[#15803D]">₹ {formatMoney(filteredTotals.in)}</span></span>
              <span>Expenses: <span className="data-num font-bold text-[#B91C1C]">₹ {formatMoney(filteredTotals.expenses)}</span></span>
              <span className="ml-auto">{filteredRows.length} entries</span>
            </div>
          </div>
        </div>
      )}

      {/* Ledger */}
      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 small-label text-gray-500">
          Cash Ledger — chronological{monthFilter ? ` · ${monthLabel(monthFilter)}` : ""}
        </div>
        <div className="overflow-auto">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Type</th>
                <th className="text-left">Description</th>
                <th className="num">In</th>
                <th className="num">Out</th>
                <th className="num">Balance</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(filteredRows || []).map((r) => (
                <tr key={`${r.ref_type}-${r.id}`}>
                  <td className="data-num text-xs">{r.date}</td>
                  <td>
                    <span className={`px-2 py-0.5 text-xs rounded-sm capitalize ${
                      r.kind === "expense" ? "stock-mt"
                      : r.kind === "customer_payment" ? "stock-filled"
                      : r.kind === "cash_in" ? "stock-filled"
                      : "stock-mt"
                    }`}>{r.kind.replace(/_/g, " ")}</span>
                  </td>
                  <td className="text-sm">
                    {r.description}
                    {(r.created_by_name || r.created_by_email) && (
                      <div className="text-[10px] text-gray-400 mt-0.5 inline-flex items-center gap-1" data-testid={`entered-by-${r.id}`}>
                        <UserCheck size={10} />
                        {r.created_by_name || r.created_by_email}
                        {r.entered_at ? ` · ${formatDateTime(r.entered_at)}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="num text-[#15803D]">{r.amount > 0 ? formatMoney(r.amount) : ""}</td>
                  <td className="num text-[#B91C1C]">{r.amount < 0 ? formatMoney(-r.amount) : ""}</td>
                  <td className="num font-bold">{formatMoney(r.balance)}</td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-2">
                      {r.ref_type === "expense" && (
                        <button onClick={() => openEdit(r)} className="text-gray-400 hover:text-black" title="Edit notes" data-testid={`edit-expense-${r.id}`}>
                          <Pencil size={15} />
                        </button>
                      )}
                      {isAdmin && r.ref_type !== "payment" && (
                        <button onClick={() => deleteRow(r)} className="text-gray-400 hover:text-[#B91C1C]" data-testid={`del-${r.id}`}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr><td colSpan="7" className="py-8 text-center text-gray-400 text-sm">
                  {monthFilter ? `No entries in ${monthLabel(monthFilter)} for this holder` : "No entries yet for this holder"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Expense Notes modal */}
      {editRow && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setEditRow(null)}>
          <form onSubmit={saveEdit} className="bg-white rounded-sm w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="edit-expense-form">
            <div className="flex items-start justify-between">
              <div>
                <div className="small-label text-gray-500">Edit notes</div>
                <h3 className="font-heading font-black text-xl">{editRow.category}</h3>
                <div className="text-xs text-gray-500 mt-1">
                  {editRow.date} · <span className="data-num font-bold">₹ {formatMoney(editRow.amount)}</span>
                </div>
              </div>
              <button type="button" onClick={() => setEditRow(null)} className="text-gray-400 hover:text-black">
                <X size={20} />
              </button>
            </div>
            <div className="text-xs text-gray-500 -mt-2">
              Only Description & Reference can be edited. Amount, date and category are immutable.
            </div>
            <div>
              <label className="small-label block mb-1">Description</label>
              <input className="input-lg" value={editRow.description}
                onChange={(e) => setEditRow({ ...editRow, description: e.target.value })}
                data-testid="edit-expense-description" />
            </div>
            <div>
              <label className="small-label block mb-1">Reference</label>
              <input className="input-lg" value={editRow.reference}
                onChange={(e) => setEditRow({ ...editRow, reference: e.target.value })}
                placeholder="bill no, vehicle, person, etc."
                data-testid="edit-expense-reference" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditRow(null)} className="h-11 px-4 border border-gray-300 rounded-sm text-sm font-semibold">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary h-11 px-5 flex items-center gap-2" data-testid="edit-expense-save">
                <Save size={16} /> Save Notes
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
